import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { AssetType, MapCategoryType, MapDetailType } from '@/models/guest-app/asset'
import { ContainerHeader } from '@/components/molecules/settings/container-header'
import { ContainerSidePanelBody } from '@/components/molecules/settings/container-side-panel-body'
import { AssetDetailContext } from '@/components/pages/setting/guest-app/asset-detail'
import { getAssetMapCategoryList, updateAsset, updateAssetMapCategoryDisplayOrder } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useDraggableDom } from '@/components/molecules/settings/use-draggable-dom'
import { MapCategoryDetailModal } from './map-category-detail-modal'
import { GuestAppTableHeader, GuestAppTableBody, GuestAppTableRow } from '@/components/atoms/settings/guest-app-table'

type Props = {
  asset: AssetType & { details: MapDetailType[] }
}

export const MapCategoryList: React.FC<Props> = ({ asset }) => {
  const { t } = useTranslation()
  const [formValue, setFormValue] = useState<{ title: string; categories: MapCategoryType[] }>({
    title: asset.title,
    categories: [],
  })
  const [showDetailProp, setShowDetailProp] = useState<MapCategoryType | undefined>(undefined)
  const { doReload, setIsLoading, setIsEditing, listenDoSave, listenDoCancel } = useContext(AssetDetailContext)
  const { errorHandler } = useErrorHandler()
  const changeDisplayOrder = (newList: MapCategoryType[]) => {
    setFormValue({ ...asset, categories: newList })
    setIsEditing(true)
  }
  const { dragStart, dragOver, dragDrop, handleTouchStart, handleTouchMove, handleTouchEnd } = useDraggableDom({
    list: formValue.categories,
    onChange: changeDisplayOrder,
  })

  const saveFormValue = async () => {
    try {
      setIsLoading(true)
      if (formValue.categories.length) {
        await updateAssetMapCategoryDisplayOrder(
          asset.id,
          formValue.categories.map(detail => detail.id),
        )
      }
      await updateAsset(asset.id, { title: formValue.title })
      doReload()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (listenDoSave) saveFormValue()
  }, [listenDoSave])

  useEffect(() => {
    if (listenDoCancel) {
      doReload()
    }
  }, [listenDoCancel])

  useEffect(() => {
    if (!asset) {
      return
    }
    getAssetMapCategoryList(asset.id).then(categories => {
      setFormValue({
        title: asset.title,
        categories,
      })
    })
  }, [asset])

  return (
    <>
      <ContainerHeader
        goBack
        title={formValue.title}
        onChangeTitle={(newTitle: string) => {
          setFormValue({ ...formValue, title: newTitle })
          setIsEditing(true)
        }}
      />
      <ContainerSidePanelBody
        menus={[
          {
            title: t('List'),
            path: `/setting/guest-app/asset/${asset.id}`,
            isActive: false,
          },
          {
            title: t('Category'),
            path: `/setting/guest-app/asset/${asset.id}/map-category`,
            isActive: true,
          },
        ]}
      >
        <>
          <GuestAppTableHeader>
            <div css={thumbnailColStyle}></div>
            <div css={titleColStyle}>{t('Title')}</div>
            <div css={deleteColStyle}></div>
          </GuestAppTableHeader>
          <GuestAppTableBody>
            {formValue.categories.map(category => (
              <GuestAppTableRow
                id={category.id}
                key={category.id}
                onClick={() => setShowDetailProp(category)}
                draggable="true"
                onDragStart={dragStart}
                onDragOver={dragOver}
                onDrop={dragDrop}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <div css={thumbnailColStyle}>
                  <img src={category.imagePath}></img>
                </div>
                <div css={titleColStyle}>{category.title}</div>
                <div css={deleteColStyle}></div>
              </GuestAppTableRow>
            ))}
          </GuestAppTableBody>
        </>
      </ContainerSidePanelBody>
      {showDetailProp !== undefined && (
        <MapCategoryDetailModal
          asset={asset}
          detail={showDetailProp}
          onCancel={() => setShowDetailProp(undefined)}
          onSave={() => setShowDetailProp(undefined)}
        />
      )}
    </>
  )
}

const thumbnailColStyle = css({
  marginLeft: 62,
  marginRight: 24,
  width: 'calc(38px * 16 / 9)',
  '> img': {
    height: 38,
    borderRadius: 5,
  },
})
const titleColStyle = css({
  flexGrow: 1,
  overflowWrap: 'anywhere',
  width: 'min-content',
})
const deleteColStyle = css({
  width: 30,
  margin: '0 16px',
})
